import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MessageCircle,
  Search,
  ChevronRight,
  Loader2,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Eye,
  Reply,
  UserCheck,
  Clock,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";

const STATUS_OPTIONS = ["Open", "In Review", "Answered", "Closed"];
const CATEGORIES = ["Contribution", "Loan", "Payroll Deduction", "Penalty", "Account", "Technical Issue", "Other"];

const STATUS_COLORS: Record<string, string> = {
  Open: "bg-blue-100 text-blue-700 border-blue-200",
  "In Review": "bg-amber-100 text-amber-700 border-amber-200",
  Answered: "bg-green-100 text-green-700 border-green-200",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdminSupportTickets() {
  const queryClient = useQueryClient();
  const [viewTicket, setViewTicket] = useState<any>(null);
  const [replyText, setReplyText] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [filters, setFilters] = useState<{ status: string; category: string; search: string }>({
    status: "",
    category: "",
    search: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: tickets, isLoading } = useQuery(
    ["admin-support-tickets", filters],
    async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.category) params.set("category", filters.category);
      if (filters.search) params.set("search", filters.search);
      const { data } = await api.get(`/admin/support-tickets?${params.toString()}`);
      return data.data || [];
    }
  );

  const updateStatusMutation = useMutation(
    async ({ id, status }: { id: number; status: string }) => {
      const { data } = await api.patch(`/admin/support-tickets/${id}/status`, { status });
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Status updated");
        queryClient.invalidateQueries(["admin-support-tickets"]);
        if (viewTicket) {
          api.get(`/admin/support-tickets/${viewTicket.id}`).then(({ data }) => setViewTicket(data.data));
        }
      },
    }
  );

  const replyMutation = useMutation(
    async () => {
      const { data } = await api.post(`/admin/support-tickets/${viewTicket.id}/replies`, {
        message: replyText,
        isInternalNote: isInternal,
      });
      return data;
    },
    {
      onSuccess: () => {
        toast.success(isInternal ? "Internal note added" : "Reply sent");
        setReplyText("");
        queryClient.invalidateQueries(["admin-support-tickets"]);
        if (viewTicket) {
          api.get(`/admin/support-tickets/${viewTicket.id}`).then(({ data }) => setViewTicket(data.data));
        }
      },
    }
  );

  const ticketList = Array.isArray(tickets) ? tickets : [];
  const openCount = ticketList.filter((t: any) => t.status === "Open").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Member Questions</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {openCount > 0 ? `${openCount} open question${openCount > 1 ? "s" : ""} awaiting response` : "All questions resolved"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                    className="pl-8 h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse h-20 bg-white rounded-xl border border-slate-100 p-4" />
          ))}
        </div>
      ) : ticketList.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <HelpCircle className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-sm font-medium text-slate-500">No support tickets found</p>
            <p className="text-xs text-slate-400 mt-1">All member questions have been resolved.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {ticketList.map((ticket: any) => (
            <div
              key={ticket.id}
              className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-slate-300 transition-colors"
              onClick={() => {
                setViewTicket(ticket);
                setReplyText("");
                setIsInternal(false);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-900">{ticket.subject}</h3>
                    <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full border", STATUS_COLORS[ticket.status] || "")}>
                      {ticket.status}
                    </span>
                    <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200">
                      {ticket.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{ticket.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                    <span className="font-medium text-slate-600">{ticket.member?.fullNames || `Member #${ticket.memberId}`}</span>
                    <span>&middot;</span>
                    <span>{formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}</span>
                    {ticket.replies?.length > 0 && (
                      <>
                        <span>&middot;</span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" />
                          {ticket.replies.length}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0 mt-1" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View/Reply Dialog */}
      <Dialog open={!!viewTicket} onOpenChange={(o) => { if (!o) setViewTicket(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <DialogTitle className="text-base">{viewTicket.subject}</DialogTitle>
                  <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full border", STATUS_COLORS[viewTicket.status] || "")}>
                    {viewTicket.status}
                  </span>
                </div>
                <DialogDescription>
                  {viewTicket.category} &middot; {viewTicket.member?.fullNames || `Member #${viewTicket.memberId}`} &middot; {format(new Date(viewTicket.createdAt), "MMM dd, yyyy HH:mm")}
                </DialogDescription>
              </DialogHeader>

              {/* Status Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_OPTIONS.filter((s) => s !== viewTicket.status).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="outline"
                    className="text-xs h-7"
                    onClick={() => updateStatusMutation.mutate({ id: viewTicket.id, status: s })}
                    disabled={updateStatusMutation.isLoading}
                  >
                    Mark {s}
                  </Button>
                ))}
              </div>

              {/* Original message */}
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewTicket.message}</p>
                </CardContent>
              </Card>

              {/* Replies */}
              {viewTicket.replies?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">Conversation</h4>
                  {viewTicket.replies.map((reply: any) => (
                    <div
                      key={reply.id}
                      className={cn(
                        "border rounded-lg p-3",
                        reply.isInternalNote ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn("text-xs font-semibold", reply.isInternalNote ? "text-amber-700" : "text-slate-700")}>
                          {reply.senderRole === "member" ? viewTicket.member?.fullNames || "Member" : reply.senderRole}
                          {reply.isInternalNote && <span className="ml-1.5 text-[10px] text-amber-500">(Internal Note)</span>}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Label>Reply</Label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded"
                    />
                    Internal note (visible to admins/leaders only)
                  </label>
                </div>
                <Textarea
                  placeholder={isInternal ? "Add internal note..." : "Type your reply to the member..."}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                />
                <div className="flex justify-end gap-2">
                  {viewTicket.status !== "Closed" && (
                    <Button
                      size="sm"
                      onClick={() => replyMutation.mutate()}
                      disabled={!replyText.trim() || replyMutation.isLoading}
                      className="bg-[#009A44] hover:bg-[#00803a] gap-2"
                    >
                      {replyMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Reply className="h-4 w-4" />}
                      {isInternal ? "Save Note" : "Send Reply"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
